import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageHeader } from '../components/page-header';
import { User as UserIcon, Heart, MessageSquare, Info, X, CheckCircle, Link2 } from 'lucide-react';
import {
  PersonInfoCard,
  PersonFormData,
  ManagerNotesSection,
  KudosSection,
  AboutSection,
  GustoUserCard,
} from '../components/person';
import { ReportingChain } from '../components/people';
import {
  useUser,
  useUsers,
  useClientTags,
  useUpdateUser,
  useUpdateUserRole,
  useUploadUserPhoto,
  useIsAdmin,
  useIsManager,
  useMyDirectReports,
  useGustoEnabled,
} from '../hooks';
import { managerNotesService, slackService } from '../services';
import { SecurityRole } from '../types';

interface ManagerNote {
  id: string;
  userId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

interface KudosItem {
  id: string;
  fromUserId: string;
  fromUserName?: string;
  fromUserPhoto?: string;
  toUserId: string;
  toUserName?: string;
  message: string;
  createdAt: string;
}

const PersonDetail: React.FC = () => {
  const { t } = useTranslation('people');
  const { id } = useParams<{ id: string }>();
  useAuth(); // keep context active
  const navigate = useNavigate();

  // Manager Notes state
  const [managerNotes, setManagerNotes] = useState<ManagerNote[]>([]);
  const [notesPage, setNotesPage] = useState(0);
  const [totalNotesPages, setTotalNotesPages] = useState(0);
  const [notesLoading, setNotesLoading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Kudos state
  const [kudos, setKudos] = useState<KudosItem[]>([]);
  const [kudosPage, setKudosPage] = useState(1);
  const [hasMoreKudos, setHasMoreKudos] = useState(true);
  const [kudosLoading, setKudosLoading] = useState(false);

  // React Query hooks
  const { data: person, isLoading, error } = useUser(id || '');
  const { data: allPeople = [] } = useUsers(true);
  const { data: allClientTags = [] } = useClientTags();
  const updateUser = useUpdateUser();
  const uploadPhoto = useUploadUserPhoto();
  const updateUserRole = useUpdateUserRole();

  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { isManager, isLoading: managerLoading } = useIsManager();
  const { data: directReports = [], isLoading: isLoadingDirectReports } = useMyDirectReports();
  const { isConnected: gustoConnected } = useGustoEnabled();
  const hasAccess = isAdmin || (isManager && directReports.some(r => r.id === id));
  const showGustoTab = (isAdmin || isManager) && gustoConnected;

  // Redirect users without access
  useEffect(() => {
    if (!adminLoading && !managerLoading && !isLoadingDirectReports && !hasAccess) {
      navigate('/dashboard');
    }
  }, [hasAccess, adminLoading, managerLoading, isLoadingDirectReports, navigate]);

  // Load manager notes only after access is confirmed
  useEffect(() => {
    if (id && hasAccess) {
      fetchManagerNotes(0);
    }
  }, [id, hasAccess]);

  // Load kudos from server only after access is confirmed
  useEffect(() => {
    if (id && hasAccess) {
      fetchKudos(1);
    }
  }, [id, hasAccess]);

  // When the users list changes, remap sender/recipient names locally without refetching kudos
  useEffect(() => {
    if (allPeople.length === 0) return;
    setKudos((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((k) => {
        const fromUser = allPeople.find((p) => p.id === k.fromUserId);
        const toUser = allPeople.find((p) => p.id === k.toUserId);
        return {
          ...k,
          fromUserName: fromUser?.name || k.fromUserName,
          toUserName: toUser?.name || k.toUserName,
        };
      });
    });
  }, [allPeople]);

  const fetchManagerNotes = async (page: number) => {
    if (!id) return;

    try {
      setNotesLoading(true);
      const response = await managerNotesService.getByUserId(id, page, 10);
      setManagerNotes(response.notes);
      setNotesPage(response.page);
      setTotalNotesPages(response.totalPages);
    } catch (error) {
      console.error('Error fetching manager notes:', error);
    } finally {
      setNotesLoading(false);
    }
  };

  const fetchKudos = async (page: number) => {
    if (!id) return;

    try {
      setKudosLoading(true);
      console.log('Fetching kudos for user ID:', id);
      const response = await slackService.getPaginatedKudos(id, undefined, page, 10);
      console.log('Kudos response:', response);
      
      const kudosWithNames = response.kudos.map((kudo) => {
        const fromUser = allPeople.find((p) => p.id === kudo.fromUserId);
        const toUser = allPeople.find((p) => p.id === kudo.toUserId);

        return {
          id: kudo.id,
          fromUserId: kudo.fromUserId,
          fromUserName: fromUser?.name || kudo.fromUserName || 'Unknown',
          fromUserPhoto: fromUser?.profilePictureUrl || fromUser?.photoURL,
          toUserId: kudo.toUserId,
          toUserName: toUser?.name || kudo.toUserName || 'Unknown',
          message: kudo.message,
          createdAt: kudo.createdAt,
        };
      });

      if (page === 1) {
        setKudos(kudosWithNames);
      } else {
        setKudos((prev) => [...prev, ...kudosWithNames]);
      }
      
      setKudosPage(response.page);
      setHasMoreKudos(response.page < response.totalPages);
    } catch (error) {
      console.error('Error fetching kudos:', error);
    } finally {
      setKudosLoading(false);
    }
  };

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSave = async (formData: PersonFormData, photo: File | null) => {
    if (!id || !person) return;

    try {
      // Update user data
      const updateData: any = {
        name: formData.name,
        organizationRole: formData.organizationRole,
        clients: formData.clients,
        isActive: formData.isActive,
      };

      // Off-boarding details are only valid for an inactive member; the date input
      // already produces a date-only YYYY-MM-DD string, so no timezone conversion.
      if (!formData.isActive) {
        if (formData.offboardingDate) {
          updateData.offboardingDate = formData.offboardingDate;
        }
        // Always send the reason: a blank value clears the stored one.
        updateData.offboardingReason = formData.offboardingReason;
      }

      // Only include email if it's different from the original
      if (formData.email !== person.email) {
        updateData.email = formData.email;
      }

      if (formData.reportsTo !== '__none__') {
        updateData.reportsToId = formData.reportsTo;
      } else {
        updateData.reportsToId = null;
      }

      if (formData.birthDate) {
        updateData.birthDate = formData.birthDate;
      }

      if (formData.hireDate) {
        updateData.hireDate = formData.hireDate;
      }

      // Add contract rate (allow zero, but convert empty string to null)
      if (formData.contractRate) {
        updateData.contractRate = parseFloat(formData.contractRate);
      } else {
        updateData.contractRate = null;
      }

      await updateUser.mutateAsync({ id, data: updateData });

      // The access role lives on the organization membership, not on the member record,
      // so it goes through its own endpoint and only when an admin actually changed it.
      if (isAdmin && formData.securityRole && formData.securityRole !== (person.securityRole || '')) {
        await updateUserRole.mutateAsync({ id, role: formData.securityRole as SecurityRole });
      }

      // Upload photo if provided
      if (photo) {
        await uploadPhoto.mutateAsync({ id, file: photo });
      }

      setSuccessMessage(t('detail.updateSuccess'));
    } catch (error) {
      console.error('Error saving person:', error);
      throw error;
    }
  };

  const handleAddNote = async (content: string) => {
    if (!id) return;

    try {
      setSavingNote(true);
      await managerNotesService.create(id, {
        content: content,
      });
      // Refresh notes
      await fetchManagerNotes(notesPage);
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    } finally {
      setSavingNote(false);
    }
  };

  const handleLoadMoreKudos = () => {
    fetchKudos(kudosPage + 1);
  };

  if (isLoading || adminLoading || managerLoading || isLoadingDirectReports) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="person-detail-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (error || !person) {
    return (
      <div className="space-y-6" data-testid="person-detail-error">
        <PageHeader title={t('detail.notFound')} description={t('detail.unableToLoad')} testId="person-detail-header" />
        <Alert variant="destructive" data-testid="person-detail-error-alert">
          <AlertDescription>
            {error?.message || t('detail.fetchError')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const saving = updateUser.isPending || updateUserRole.isPending || uploadPhoto.isPending;

  return (
    <div className="space-y-6" data-testid="person-detail-page">
      {successMessage && (
        <Alert variant="success" data-testid="person-detail-success-alert" className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{successMessage}</AlertDescription>
          </div>
          <button
            onClick={() => setSuccessMessage(null)}
            className="p-1 hover:bg-green-200 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}
      <PageHeader
        title={person.name}
        description={person.email}
        testId="person-detail-header"
      />

      <Tabs defaultValue="profile" className="space-y-4" data-testid="person-detail-tabs">
        <TabsList data-testid="person-detail-tabs-list">
          <TabsTrigger value="profile" className="flex items-center gap-2" data-testid="person-detail-tab-profile">
            <UserIcon className="h-4 w-4" />
            {t('detail.tabs.profile')}
          </TabsTrigger>
          <TabsTrigger value="about" className="flex items-center gap-2" data-testid="person-detail-tab-about">
            <Info className="h-4 w-4" />
            About
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="person-detail-tab-notes">
            <MessageSquare className="h-4 w-4" />
            {t('detail.tabs.managerNotes')}
          </TabsTrigger>
          <TabsTrigger value="kudos" className="flex items-center gap-2" data-testid="person-detail-tab-kudos">
            <Heart className="h-4 w-4" />
            {t('detail.tabs.kudos')}
          </TabsTrigger>
          {showGustoTab && (
            <TabsTrigger value="gusto" className="flex items-center gap-2" data-testid="person-detail-tab-gusto">
              <Link2 className="h-4 w-4" />
              {t('detail.tabs.gusto')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" data-testid="person-detail-content-profile">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-9">
              <PersonInfoCard
                person={person}
                allPeople={allPeople}
                allClientTags={allClientTags}
                onSave={handleSave}
                saving={saving}
                canEditSecurityRole={isAdmin}
              />
            </div>
            <div className="lg:col-span-3 space-y-6">
              <ReportingChain 
                userId={id} 
                subject={{
                  id: person.id,
                  name: person.name,
                  profilePictureUrl: person.profilePictureUrl,
                  organizationRole: person.organizationRole,
                }}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="about" data-testid="person-detail-content-about">
          <AboutSection person={person} />
        </TabsContent>

        <TabsContent value="notes" data-testid="person-detail-content-notes">
          <ManagerNotesSection
            notes={managerNotes}
            loading={notesLoading}
            currentPage={notesPage}
            totalPages={totalNotesPages}
            onPageChange={fetchManagerNotes}
            onAddNote={handleAddNote}
            saving={savingNote}
          />
        </TabsContent>

        <TabsContent value="kudos" data-testid="person-detail-content-kudos">
          <KudosSection
            kudos={kudos}
            loading={kudosLoading}
            hasMore={hasMoreKudos}
            onLoadMore={handleLoadMoreKudos}
            users={allPeople}
          />
        </TabsContent>

        {showGustoTab && (
          <TabsContent value="gusto" data-testid="person-detail-content-gusto">
            <GustoUserCard person={person} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default PersonDetail;
